import { useEffect, useRef, useState } from "react";
import { Controller, useController, useFormContext } from "react-hook-form";
import { useRequiredAdSelection } from "../../../../pages/NewAdsCampaign/hooks/requiredAdsCampaignSelection";
import useFocusErrorForm from "../../../../hooks/useFocusErrorForm";
import useApprovalFieldNavigateTab from "../../../../pages/NewAdsCampaign/hooks/useApprovalFieldNavigateTab";
import {
  ADS_CAMPAIGN_META_CREATIVE_SOURCE_TYPES,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION,
  ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_META,
  ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  ADS_CAMPAIGN_STATUS_DRAFT,
  ALERT_ICON_TYPE_SUCCESS,
  IMAGE_FORMATS,
  META,
  MIN_DIMENSIONS_CONTENT,
  MIN_WIDTH_SCREEN_TO_SHOW_CHAT,
  VIDEO_FORMATS,
} from "../../../../utils/constants";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import { getIsBlockedActions } from "../../../../actions/getters";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useDispatchApp } from "../../../../lib/redux";
import { generalFormatMediaUrl } from "../../../../services/adsCampaigns";
import {
  generateAdsCampaignDescription,
  startDeleteAdMediaID,
} from "../../../../actions/adsCampaigns";
import { getUniqueID } from "../../../../utils/numbers";
import { useMediaQuery } from "@mui/material";
import { useTranslationApp } from "../../../../lib/i18next";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import {
  AD_SCHEDULE_PLACEHOLDER,
  ADS_CAMPAIGN_PREVIEW,
  ADVANTAGE_CATALOG_PLACEHOLDER,
  BUTTON_CHANGE_SLIDE,
  CALL_TO_ACTION_PLACEHOLDER,
  CARD,
  CAROUSEL,
  CONTENT,
  COPY_VALUES_IN_OTHER_ADS,
  CREATIVE_SOURCE_PLACEHOLDER,
  CREATIVE_TYPE_PLACEHOLDER,
  DEEP_DESTINATION_APP_URL_PLACEHOLDER,
  FIELD_MIN_CARDS_AD_CAROUSEL,
  FIELD_REQUIRED,
  HEADLINE_PLACEHOLDER,
  HEADLINES,
  INDIVIDUAL,
  MANUAL_UPLOAD_PLACEHOLDER,
  READY,
  TITLE_PLACEHOLDER,
  TITLES_META,
  TRACKING_EVENTS,
  TRACKING_OFFLINE_EVENTS_PLACEHOLDER,
  TRACKING_WEBSITE_EVENTS,
} from "../../../../i18n/keysTranslations";
import ContentField from "../../../Form/ContentField";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "../../../Form/TextField";
import { waitDelay } from "../../../../utils/date";
import Select from "../../../Form/Select";
import EditPlacementsMeta from "./EditPlacementsMeta";
import EditAdvantageEnhances from "./EditAdvantageEnhances";
import ClickableText from "../../../Texts/ClickableText";
import { SimpleAlert } from "../../../Alerts/Alerts";
import SelectCallToActionMeta from "./SelectCallToActionMeta";
import ChipOptional from "../../../Chip/ChipOptional";
import {
  getShowAdScheduleMeta,
  MAP_CUSTOM_PAGE_ID_PLACEHOLDER,
} from "../../../../services/meta";
import {
  BOX_SHADOW_FIELDS,
  INPUT_COLOR_BORDER,
} from "../../../../utils/colors";
import { Tabs, Tab } from "@mui/material";
import { Add } from "@mui/icons-material";
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import SelectTrackingIDField from "../../../Form/SelectTrackingIDField";
import AdScheduleContainerMeta from "./AdScheduleContainerMeta";
import AdPreviewMeta from "./AdPreviewMeta";
import CardContainer from "../../../Card/CardContainer";
import CardContainerWithCopy from "../../../Card/CardContainerWithCopy";
import TextFieldWithActions from "../../../Form/TextFieldWithActions";
import { alertIfEmptyCopyValue } from "../../../../utils/copyFormValue";
import {
  commitGeneratedSectionText,
  copySectionValue,
  readStoredSectionFingerprint,
} from "../../../../utils/aiGeneratedForm";

const platform = META;
const grid = {
  xs: 12,
  lg: 6,
};

const maxTitlesPlatform = 5;
const maxHeadlinesPlatform = 5;

const AdAppPromotionMetaForm = () => {
  const refPreview = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    watch,
    getValues,
    control,
    formState,
    sectionSelected,
    isGeneralDisabledFields,
    setValue,
    trigger,
    isChatVisible,
  } = useFormContext();
  useFocusErrorForm(formState);

  const breakColumnFormAndPreview = useMediaQuery(() => "(max-width: 1080px)");
  const isLargeScreen = useMediaQuery(
    () => `(min-width: ${MIN_WIDTH_SCREEN_TO_SHOW_CHAT}px)`
  );
  const showSlideChat = isChatVisible && isLargeScreen;

  const errors = formState.errors;
  const {
    adGroup,
    adID,
    indexAdGroup,
    indexAd,
    BASIC_PATH,
    BASIC_PATH_AD_GROUP,
  } = useRequiredAdSelection();
  const isIncompatibleAd = getValues(`${BASIC_PATH}.isIncompatible`) || false;
  const isCarousel = watch(`${BASIC_PATH}.isCarousel`);
  const platformData = watch(`platformsData.${platform}`);
  const objective = platformData?.objective;

  //Delete to test
  const showCreativeSource =
    !platformData?.isCatalogCampaign &&
    objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION;

  const isDisabledFields =
    isGeneralDisabledFields ||
    platformData?.isIncompatible ||
    adGroup?.isIncompatible ||
    isIncompatibleAd;

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const { ADS_CAMPAIGN_GENERATE_TEXT_ACTION } = useCreditActionCosts();

  const onGenerateText = async ({ type, index }) => {
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_TEXT_ACTION,
        showUsageConfirmationAlert: true,
      })
    );

    if (isBlocked) {
      return;
    }

    const currentTexts = getValues(`${BASIC_PATH}.${type}`) || [];
    const currentText = currentTexts[index]?.value || "";
    const allTexts = currentTexts.map((item) => item.value || "");
    const textsWithCurrentFirst = [
      currentText,
      ...allTexts.filter((_, i) => i !== index),
    ];

    const response = await dispatch(
      generateAdsCampaignDescription({
        campaignID: getValues("id"),
        objective,
        platform,
        campaignContext: getValues("context") || "",
        destinationUrl:
          getValues(`${BASIC_PATH}.destinationUrls`)?.[0]?.value || "",
        language: getValues(`language`),
        type,
        currentTexts: textsWithCurrentFirst,
        entityType: "ad",
        currentFingerprint: readStoredSectionFingerprint({
          getValues,
          basePath: BASIC_PATH,
          section: type,
        }),
      })
    );

    commitGeneratedSectionText({
      getValues,
      setValue,
      trigger,
      basePath: BASIC_PATH,
      section: type,
      index,
      platform,
      entityType: "ad",
      response,
      onAfterCommit: onGeneratePreview,
    });
  };

  const onAddText = async ({ type }) => {
    const currentTexts = getValues(`${BASIC_PATH}.${type}`) || [];
    setValue(`${BASIC_PATH}.${type}`, [
      ...currentTexts,
      {
        value: "",
        id: getUniqueID(),
      },
    ]);
    onGeneratePreview();
  };

  const onRemoveText = async ({ type, index }) => {
    const currentTexts = getValues(`${BASIC_PATH}.${type}`);
    setValue(
      `${BASIC_PATH}.${type}`,
      currentTexts.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    trigger(`${BASIC_PATH}.${type}`);
    onGeneratePreview();
  };

  const onChangeIsCarousel = async (value) => {
    const ads = getValues(`adsGroups.[${indexAdGroup}].ads`);
    const id = getValues(`${BASIC_PATH}.id`);
    setValue(
      `${BASIC_PATH}.destinationType`,
      ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION,
      { shouldValidate: true, shouldDirty: true }
    );
    if (value) {
      let mediaData = [];
      let mediaUrls = [];
      let mediaIDs = [];
      let newDestinationUrls = [];

      const destinationUrls = getValues(`${BASIC_PATH}.destinationUrls`);
      const destinationUrl = destinationUrls?.[0]?.value || "";

      ads.forEach((ad) => {
        ad.mediaUrls.forEach((mediaUrl, index) => {
          mediaUrls.push(mediaUrl);
          mediaData.push(ad?.mediaData?.[index] || { from: "upload" });
          mediaIDs.push(ad?.mediaIDs?.[index] || "");
          newDestinationUrls.push({ value: destinationUrl, id: getUniqueID() });
        });
      });

      const validateOpts = { shouldValidate: true, shouldDirty: true };
      setValue(`${BASIC_PATH}.mediaUrls`, mediaUrls, validateOpts);
      setValue(`${BASIC_PATH}.mediaData`, mediaData, validateOpts);
      setValue(`${BASIC_PATH}.mediaIDs`, mediaIDs, validateOpts);
      setValue(
        `${BASIC_PATH}.destinationUrls`,
        newDestinationUrls,
        validateOpts
      );
    } else {
      const ad = ads.find((ad) => ad.id === id);
      setValue(`${BASIC_PATH}.mediaUrls`, [ad?.mediaUrls?.[0] || ""], {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(
        `${BASIC_PATH}.mediaData`,
        [ad?.mediaData?.[0] || { from: "upload" }],
        { shouldValidate: true, shouldDirty: true }
      );
      setValue(`${BASIC_PATH}.mediaIDs`, [ad?.mediaIDs?.[0] || ""], {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(
        `${BASIC_PATH}.destinationUrls`,
        [ad?.destinationUrls?.[0] || ""],
        { shouldValidate: true, shouldDirty: true }
      );
      setValue(
        `${BASIC_PATH}.titles`,
        ad?.titles.slice(0, 3) || [{ value: "", id: getUniqueID() }],
        { shouldValidate: true, shouldDirty: true }
      );
      setValue(
        `${BASIC_PATH}.headlines`,
        ad?.headlines.slice(0, 3) || [{ value: "", id: getUniqueID() }],
        { shouldValidate: true, shouldDirty: true }
      );
      setValue(
        `${BASIC_PATH}.descriptions`,
        ad?.descriptions.slice(0, 3) || [{ value: "", id: getUniqueID() }],
        { shouldValidate: true, shouldDirty: true }
      );
    }
    setValue(`${BASIC_PATH}.isCarousel`, value, {
      shouldValidate: true,
      shouldDirty: true,
    });
    return true;
  };

  const onChangeCreativeSource = (value) => {
    setValue(`${BASIC_PATH}.isCarousel`, false);
    setValue(`${BASIC_PATH}.isInstantExperience`, false);

    setValue(`${BASIC_PATH}.mediaUrls`, [""]);
    setValue(`${BASIC_PATH}.mediaData`, [{ from: "upload" }]);

    if (ADS_CAMPAIGN_META_CREATIVE_SOURCE_TYPES.ADVANTAGE_CATALOG === value) {
      setValue(
        `${BASIC_PATH}.titles`,
        getValues(`${BASIC_PATH}.titles`).slice(0, 1),
        { shouldValidate: true, shouldDirty: true }
      );
      setValue(
        `${BASIC_PATH}.descriptions`,
        getValues(`${BASIC_PATH}.descriptions`).slice(0, 1),
        { shouldValidate: true, shouldDirty: true }
      );
      setValue(
        `${BASIC_PATH}.headlines`,
        getValues(`${BASIC_PATH}.headlines`).slice(0, 1),
        { shouldValidate: true, shouldDirty: true }
      );
    }

    if (ADS_CAMPAIGN_META_CREATIVE_SOURCE_TYPES.MANUAL === value) {
      setValue(
        `${BASIC_PATH}.titles`,
        getValues(`${BASIC_PATH}.titles`).slice(0, 3),
        { shouldValidate: true, shouldDirty: true }
      );
      setValue(
        `${BASIC_PATH}.descriptions`,
        getValues(`${BASIC_PATH}.descriptions`).slice(0, 3),
        { shouldValidate: true, shouldDirty: true }
      );
      setValue(
        `${BASIC_PATH}.headlines`,
        getValues(`${BASIC_PATH}.headlines`).slice(0, 3),
        { shouldValidate: true, shouldDirty: true }
      );
    }

    onGeneratePreview();
    setValue(`${BASIC_PATH}.creativeSource`, value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onCopyValuesInOtherAds = ({ type }) => {
    const value = getValues(`${BASIC_PATH}.${type}`);
    if (alertIfEmptyCopyValue({ value, t })) return;
    const adsOnAdgroup = getValues(`${BASIC_PATH_AD_GROUP}.ads`) || [];

    adsOnAdgroup.forEach((ad, currentIndexAd) => {
      if (ad.id === adID) return;

      copySectionValue({
        setValue,
        getValues,
        sourceBasePath: BASIC_PATH,
        section: type,
        platform,
        entityType: "ad",
        path: `${BASIC_PATH_AD_GROUP}.ads.[${currentIndexAd}].${type}`,
        value,
      });
    });

    SimpleAlert({
      title: t(READY),
      icon: ALERT_ICON_TYPE_SUCCESS,
    });
  };

  const onGeneratePreview = () => {
    if (refPreview?.current) {
      refPreview?.current?.generatePreview();
    }
  };

  //To reset values component when change selectedLevel
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await waitDelay(1);
      setIsLoading(false);
    })();
  }, [sectionSelected]);

  useEffect(() => {
    if (!showCreativeSource) return;

    const creativeSource = getValues(`${BASIC_PATH}.creativeSource`);
    if (!creativeSource)
      setValue(
        `${BASIC_PATH}.creativeSource`,
        ADS_CAMPAIGN_META_CREATIVE_SOURCE_TYPES.MANUAL,
        { shouldValidate: true, shouldDirty: true }
      );
    // eslint-disable-next-line
  }, []);

  if (isLoading) return null;

  return (
    <>
      {isCarousel && (
        <AdsAdCarouselForm
          platform={platform}
          BASIC_PATH_AD_GROUP={BASIC_PATH_AD_GROUP}
          BASIC_PATH={BASIC_PATH}
          gridItems={grid}
          onAddText={onAddText}
          onRemoveText={onRemoveText}
          onGenerateText={onGenerateText}
          isGeneralDisabledFields={isDisabledFields}
          errors={errors}
          control={control}
          refPreview={refPreview}
          breakColumnFormAndPreview={breakColumnFormAndPreview}
          platformData={platformData}
          objective={objective}
          onGeneratePreview={onGeneratePreview}
          isCarousel={isCarousel}
          onChangeIsCarousel={onChangeIsCarousel}
          onCopyValuesInOtherAds={onCopyValuesInOtherAds}
          onChangeCreativeSource={onChangeCreativeSource}
          showCreativeSource={showCreativeSource}
          indexAdGroup={indexAdGroup}
          indexAd={indexAd}
          showSlideChat={showSlideChat}
        />
      )}
      {!isCarousel && (
        <AdsAdNormalForm
          platform={platform}
          platformData={platformData}
          BASIC_PATH_AD_GROUP={BASIC_PATH_AD_GROUP}
          BASIC_PATH={BASIC_PATH}
          gridItems={grid}
          onAddText={onAddText}
          onRemoveText={onRemoveText}
          onGenerateText={onGenerateText}
          isGeneralDisabledFields={isDisabledFields}
          errors={errors}
          control={control}
          refPreview={refPreview}
          breakColumnFormAndPreview={breakColumnFormAndPreview}
          objective={objective}
          isCarousel={isCarousel}
          onChangeIsCarousel={onChangeIsCarousel}
          onGeneratePreview={onGeneratePreview}
          onCopyValuesInOtherAds={onCopyValuesInOtherAds}
          onChangeCreativeSource={onChangeCreativeSource}
          showCreativeSource={showCreativeSource}
          showSlideChat={showSlideChat}
        />
      )}
    </>
  );
};

const AdsAdCarouselForm = ({
  objective,
  platform,
  platformData,
  breakColumnFormAndPreview,
  BASIC_PATH_AD_GROUP,
  BASIC_PATH,
  onAddText,
  onRemoveText,
  onGenerateText,
  isGeneralDisabledFields,
  errors,
  control,
  refPreview,
  onGeneratePreview,
  onChangeIsCarousel,
  onCopyValuesInOtherAds,
  onChangeCreativeSource,
  showCreativeSource,
  indexAdGroup,
  indexAd,
  showSlideChat,
}) => {
  const { watch, setValue } = useFormContext();
  const adID = watch(`${BASIC_PATH}.id`);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [tabIndex, setTabIndex] = useState(0);

  useApprovalFieldNavigateTab({
    formPath: BASIC_PATH,
    onSelectIndex: setTabIndex,
  });

  useEffect(() => {
    const fields = ["destinationUrls", "headlines", "descriptions"];
    let firstErrorIndex = null;
    for (const field of fields) {
      const fieldErrors =
        errors?.adsGroups?.[indexAdGroup]?.ads?.[indexAd]?.[field];
      if (Array.isArray(fieldErrors)) {
        const idx = fieldErrors.findIndex((item) => item && item.value);
        if (idx !== -1 && (firstErrorIndex === null || idx < firstErrorIndex)) {
          firstErrorIndex = idx;
        }
      }
    }
    if (firstErrorIndex !== null && tabIndex !== firstErrorIndex) {
      setTabIndex(firstErrorIndex);
    }
    //eslint-disable-next-line
  }, [errors, indexAdGroup, indexAd]);

  const handleChangeTab = (event, newValue) => {
    setTabIndex(newValue);
  };

  const errorMediaUrl = getReactHookFormNestedError(
    errors,
    `${BASIC_PATH}.mediaUrls`
  );

  const maxLengthTitle = ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform];
  const maxLengthHeadline = ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[platform];

  const maxTitles = maxTitlesPlatform;
  const isAppPromotionCampaign =
    objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION;

  const isIOS14Campaign = platformData?.isIOS14Campaign;
  const destinationType = watch(`${BASIC_PATH_AD_GROUP}.destinationType`);
  const websiteURL = watch("websiteURL");
  const socialNetworkURL = watch("socialNetworkURL");
  const productContext = watch("productContext");
  const targetContext = watch("targetContext");
  const language = watch("language");
  const ageRange = watch(`${BASIC_PATH_AD_GROUP}.ageRange`);
  const genders = watch(`${BASIC_PATH_AD_GROUP}.genders`);
  const mediaUrls = watch(`${BASIC_PATH}.mediaUrls`) || [""];
  const mediaData = watch(`${BASIC_PATH}.mediaData`) || [{}];
  const callToAction = watch(`${BASIC_PATH}.callToAction`);
  const titles = watch(`${BASIC_PATH}.titles`) || [{ value: "" }];
  const headlines = watch(`${BASIC_PATH}.headlines`) || [{ value: "" }];
  const descriptions = watch(`${BASIC_PATH}.descriptions`) || [{ value: "" }];
  const destinationUrls = watch(`${BASIC_PATH}.destinationUrls`) || [
    { value: "" },
  ];
  const status = watch(`${BASIC_PATH}.status`);
  const isAdCreated = status !== ADS_CAMPAIGN_STATUS_DRAFT;
  const platformsData = watch("platformsData") || {};

  const showTrackingID = !(
    ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_META.includes(objective) &&
    destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION &&
    !platformData.isCatalogCampaign
  );

  useController({
    name: `${BASIC_PATH}.mediaUrls`,
    control,
    rules: {
      required: {
        value: true,
        message: t(FIELD_REQUIRED),
      },
      validate: (value) => {
        if (value.length < 2) {
          return t(FIELD_MIN_CARDS_AD_CAROUSEL, {
            value: 2,
          });
        }
      },
    },
  });

  const showDeleteCardIcon = mediaUrls?.length > 2;
  const showAddCardIcon = mediaUrls?.length < 10;

  const onDuplicateCard = async (index) => {
    const mediaUrlItem = mediaUrls[index];
    const mediaDataItem = mediaData[index];
    const headlineItem = headlines[index];
    const descriptionItem = descriptions[index];
    const destinationUrlItem = destinationUrls[index];

    setValue(`${BASIC_PATH}.mediaUrls`, [...mediaUrls, mediaUrlItem], {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`${BASIC_PATH}.mediaData`, [...mediaData, { ...mediaDataItem }], {
      shouldValidate: true,
      shouldDirty: true,
    });

    const newHeadline =
      headlineItem && typeof headlineItem === "object"
        ? { id: getUniqueID(), value: headlineItem?.value || "" }
        : { id: getUniqueID(), value: headlineItem || "" };
    setValue(`${BASIC_PATH}.headlines`, [...headlines, newHeadline], {
      shouldValidate: true,
      shouldDirty: true,
    });

    const newDescription =
      descriptionItem && typeof descriptionItem === "object"
        ? { id: getUniqueID(), value: descriptionItem?.value || "" }
        : { id: getUniqueID(), value: descriptionItem || "" };
    setValue(`${BASIC_PATH}.descriptions`, [...descriptions, newDescription], {
      shouldValidate: true,
      shouldDirty: true,
    });

    const newDestinationUrl =
      destinationUrlItem && typeof destinationUrlItem === "object"
        ? { id: getUniqueID(), value: destinationUrlItem?.value || "" }
        : { id: getUniqueID(), value: destinationUrlItem || "" };
    setValue(
      `${BASIC_PATH}.destinationUrls`,
      [...destinationUrls, newDestinationUrl],
      { shouldValidate: true, shouldDirty: true }
    );

    setTabIndex(mediaUrls.length);
    onGeneratePreview();
  };
  const onRemoveCard = async (index) => {
    setValue(
      `${BASIC_PATH}.mediaUrls`,
      mediaUrls.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    setValue(
      `${BASIC_PATH}.mediaData`,
      mediaData.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    setValue(
      `${BASIC_PATH}.headlines`,
      headlines.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    setValue(
      `${BASIC_PATH}.descriptions`,
      descriptions.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    setValue(
      `${BASIC_PATH}.destinationUrls`,
      destinationUrls.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    if (index === tabIndex && index > 0) {
      setTabIndex(index - 1);
    } else if (index < tabIndex) {
      setTabIndex(tabIndex - 1);
    }
    onGeneratePreview();
  };

  const showAdSchedule = getShowAdScheduleMeta({ objective });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: breakColumnFormAndPreview ? "column" : "row",
        gap: 3,
        width: "100%",
      }}
    >
      {/* FORM */}
      <Grid
        container
        spacing={3}
        sx={{
          height: "min-content",
        }}
      >
        {/* Card Creative Source */}
        {showCreativeSource && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }} variant="h6">
                    {t(CREATIVE_SOURCE_PLACEHOLDER)}
                  </Typography>
                </Box>
              }
            >
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name={`${BASIC_PATH}.creativeSource`}
                  render={({ field }) => (
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={field.value}
                        onChange={(e) => {
                          onChangeCreativeSource(e.target.value);
                        }}
                      >
                        <FormControlLabel
                          value={ADS_CAMPAIGN_META_CREATIVE_SOURCE_TYPES.MANUAL}
                          control={<Radio />}
                          label={<Box>{t(MANUAL_UPLOAD_PLACEHOLDER)}</Box>}
                        />
                        <FormControlLabel
                          value={
                            ADS_CAMPAIGN_META_CREATIVE_SOURCE_TYPES.ADVANTAGE_CATALOG
                          }
                          control={<Radio />}
                          label={<Box>{t(ADVANTAGE_CATALOG_PLACEHOLDER)}</Box>}
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid>
            </CardContainer>
          </Grid>
        )}

        {/* Card: Creative Type */}
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 600 }} variant="h6">
                  {t(CREATIVE_TYPE_PLACEHOLDER)}
                </Typography>
              </Box>
            }
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Select
                disabled={isGeneralDisabledFields || isAdCreated}
                variant="filled"
                name={`${BASIC_PATH}.isCarousel`}
                actionsChange={(value) => {
                  onChangeIsCarousel(value);
                }}
                doTranslate={true}
                control={control}
                options={[
                  {
                    value: false,
                    label: INDIVIDUAL,
                  },
                  {
                    value: true,
                    label: CAROUSEL,
                  },
                ]}
              />
            </Box>
          </CardContainer>
        </Grid>

        {/* Card 2: Tabs Carrousel Content */}
        <Grid item xs={12}>
          {errorMediaUrl && (
            <Grid item xs={12}>
              <Typography
                name={`${BASIC_PATH}.mediaUrls`}
                variant="body1"
                color="error"
                sx={{
                  mt: 2,
                  mb: -1,
                }}
              >
                {errorMediaUrl.message}
              </Typography>
            </Grid>
          )}

          {/* Preview Tabs */}
          <Grid item xs={12}>
            <Tabs
              value={tabIndex}
              onChange={handleChangeTab}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Media tabs"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTab-root": {
                  color: "#000 !important",
                  "&.Mui-selected": {
                    color: "#000 !important",
                    backgroundColor: "#fff",
                    borderRadius: "4px 4px 0 0",
                  },
                  "&:hover": {
                    backgroundColor: "#fff",
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "white",
                },
              }}
            >
              {mediaUrls?.map((_, index) => (
                <Tab key={index} label={`${t(CARD)} ${index + 1}`} />
              ))}
            </Tabs>
          </Grid>

          {/* Preview Cards Tabs*/}
          <Grid item xs={12}>
            <Box
              sx={{
                position: "relative",
                minHeight: "400px",
                transition: "all 0.3s ease-in-out",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                "& > *": {
                  position: "relative",
                  opacity: 1,
                  visibility: "visible",
                  transition: "opacity 0.3s ease-in-out",
                },
              }}
            >
              {mediaUrls?.map((media, index) => {
                const headline = headlines[index] || { value: "" };
                const destinationUrl = destinationUrls[index] || { value: "" };
                const mediaDataItem = mediaData?.[index] || {};

                const tabKey = destinationUrl?.id || `${index}`;

                return (
                  <Box
                    key={tabKey}
                    sx={{
                      display: index === tabIndex ? "block" : "none",
                      width: "100%",
                    }}
                  >
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          background: "background.paper",
                          border: `1px solid ${INPUT_COLOR_BORDER}`,
                          boxShadow: BOX_SHADOW_FIELDS,
                          p: 2,
                          borderRadius: 2,
                          height: "100%",
                        }}
                      >
                        {!isGeneralDisabledFields && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center",
                            }}
                          >
                            {showAddCardIcon && (
                              <IconButton
                                type="button"
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  onDuplicateCard(index);
                                }}
                              >
                                <Add />
                              </IconButton>
                            )}
                            {showDeleteCardIcon && (
                              <IconButton
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  onRemoveCard(index);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        )}

                        <Grid container spacing={3} sx={{ pb: 2, px: 2 }}>
                          <Grid item xs={12} md={8}>
                            <Grid container spacing={0}>
                              {!isIOS14Campaign && (
                                <Grid item xs={12} sx={{ mb: 3 }}>
                                  <TextFieldWithActions
                                    labelTitle={t(
                                      DEEP_DESTINATION_APP_URL_PLACEHOLDER
                                    )}
                                    key={destinationUrl.id}
                                    type="destinationUrl"
                                    label={""}
                                    name={`${BASIC_PATH}.destinationUrls.[${index}].value`}
                                    disabled={isGeneralDisabledFields}
                                    platform={platform}
                                    showAddIcon={false}
                                    showDeleteIcon={false}
                                    showCounterCharacters={false}
                                    onChange={onGeneratePreview}
                                    control={control}
                                    errors={errors}
                                  />
                                </Grid>
                              )}
                              <Grid item xs={12} id={`${BASIC_PATH}.headlines`}>
                                <TextFieldWithActions
                                  labelTitle={t(HEADLINE_PLACEHOLDER)}
                                  key={headline.id}
                                  type="headlines"
                                  label={""}
                                  name={`${BASIC_PATH}.headlines.[${index}].value`}
                                  disabled={isGeneralDisabledFields}
                                  platform={platform}
                                  showAddIcon={false}
                                  showDeleteIcon={false}
                                  maxLength={maxLengthHeadline}
                                  onChange={onGeneratePreview}
                                  onAdd={() => onAddText({ type: "headlines" })}
                                  onRemove={() =>
                                    onRemoveText({ type: "headlines", index })
                                  }
                                  onGenerate={async () => {
                                    await onGenerateText({
                                      type: "headlines",
                                      index,
                                    });
                                  }}
                                  control={control}
                                  errors={errors}
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <ContentField
                                  platform={META}
                                  previewMode="carouselCard"
                                  buttonTextChange={BUTTON_CHANGE_SLIDE}
                                  propsSelectContentComponent={{
                                    language,
                                    acceptedFormats:
                                      VIDEO_FORMATS.concat(IMAGE_FORMATS),
                                    showButtonUpload: true,
                                    showGenerateWithAIOption:
                                      destinationType !==
                                      ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION,
                                    showUploadDeviceOption:
                                      destinationType !==
                                      ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION,
                                    showGalleryOption:
                                      destinationType !==
                                      ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION,
                                    propsPostsAdsPlatforms: platformsData,
                                    destinationType,
                                    propsGenerateAI: {
                                      videoData: mediaDataItem?.videoID
                                        ? {
                                            ...mediaDataItem,
                                            id: mediaDataItem?.videoID,
                                          }
                                        : null,
                                      websiteURL,
                                      socialNetworkURL,
                                      language,
                                      integrationID:
                                        platformData?.integrationID,
                                      adAccountID: platformData?.adAccountID,
                                      campaignContext: {
                                        objective,
                                        productContext,
                                        targetContext,
                                        genders,
                                        ageRange,
                                      },
                                    },
                                  }}
                                  minDimensions={MIN_DIMENSIONS_CONTENT}
                                  onCallbackSave={({ from, content }) => {
                                    const contentFormatted =
                                      generalFormatMediaUrl({
                                        content,
                                        from,
                                      });
                                    dispatch(
                                      startDeleteAdMediaID({
                                        adID,
                                        index,
                                      })
                                    );
                                    setValue(
                                      `${BASIC_PATH}.mediaIDs.[${index}]`,
                                      ""
                                    );
                                    setValue(
                                      `${BASIC_PATH}.mediaUrls.[${index}]`,
                                      contentFormatted.url,
                                      {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                      }
                                    );
                                    setValue(
                                      `${BASIC_PATH}.mediaData.[${index}]`,
                                      contentFormatted.mediaData,
                                      {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                      }
                                    );
                                    onGeneratePreview();
                                  }}
                                  label={t(CONTENT)}
                                  name={`${BASIC_PATH}.mediaUrls.[${index}]`}
                                  control={control}
                                  errors={errors}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          </Grid>
        </Grid>

        {/* Card 2: Call to Action */}
        <Grid Grid item xs={12}>
          <CardContainer
            aiGeneratedSection={{
              basePath: BASIC_PATH,
              section: "callToAction",
            }}
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 600 }} variant="h6">
                  {t(CALL_TO_ACTION_PLACEHOLDER)}
                </Typography>
              </Box>
            }
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <SelectCallToActionMeta
                disabled={isGeneralDisabledFields}
                name={`${BASIC_PATH}.callToAction`}
                fullWidth
                objective={objective}
                platform={platform}
                destinationType={destinationType}
                doTranslate={true}
                control={control}
                errors={errors}
              />
              <ClickableText
                disabled={isGeneralDisabledFields}
                onClick={() => onCopyValuesInOtherAds({ type: "callToAction" })}
              >
                {t(COPY_VALUES_IN_OTHER_ADS)}
              </ClickableText>
            </Box>
          </CardContainer>
        </Grid>

        {/* Card 3: Map Custom Page ID */}
        {isAppPromotionCampaign && (
          <Grid Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }} variant="h6">
                    {t(MAP_CUSTOM_PAGE_ID_PLACEHOLDER[destinationType])}
                  </Typography>
                  <ChipOptional size="small" />
                </Box>
              }
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <TextField
                  name={`${BASIC_PATH}.appProductPageID`}
                  variant="filled"
                  fullWidth
                  disabled={isGeneralDisabledFields}
                  control={control}
                  errors={errors}
                />
                <ClickableText
                  disabled={isGeneralDisabledFields}
                  onClick={() =>
                    onCopyValuesInOtherAds({ type: "appProductPageID" })
                  }
                >
                  {t(COPY_VALUES_IN_OTHER_ADS)}
                </ClickableText>
              </Box>
            </CardContainer>
          </Grid>
        )}

        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(TRACKING_EVENTS)}
                </Typography>
                <ChipOptional size="small" />
              </Box>
            }
          >
            <Grid container spacing={2}>
              {showTrackingID && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1">
                      {t(TRACKING_WEBSITE_EVENTS)}
                    </Typography>
                    <SelectTrackingIDField
                      name={`${BASIC_PATH}.trackingID`}
                      disabled={isGeneralDisabledFields}
                      label={""}
                      fullWidth
                      variant="filled"
                      platform={platform}
                      integrationID={platformData?.integrationID}
                      adAccountID={platformData?.adAccountID}
                      control={control}
                      errors={errors}
                    />
                    <ClickableText
                      disabled={isGeneralDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "trackingID" })
                      }
                    >
                      {t(COPY_VALUES_IN_OTHER_ADS)}
                    </ClickableText>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography variant="body1">
                    {t(TRACKING_OFFLINE_EVENTS_PLACEHOLDER)}
                  </Typography>
                  <SelectTrackingIDField
                    name={`${BASIC_PATH}.trackingOfflineID`}
                    disabled={isGeneralDisabledFields}
                    label={""}
                    fullWidth
                    variant="filled"
                    platform={platform}
                    integrationID={platformData?.integrationID}
                    adAccountID={platformData?.adAccountID}
                    control={control}
                    errors={errors}
                  />
                  <ClickableText
                    disabled={isGeneralDisabledFields}
                    onClick={() =>
                      onCopyValuesInOtherAds({ type: "trackingOfflineID" })
                    }
                  >
                    {t(COPY_VALUES_IN_OTHER_ADS)}
                  </ClickableText>
                </Box>
              </Grid>
            </Grid>
          </CardContainer>
        </Grid>

        {showAdSchedule && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t(AD_SCHEDULE_PLACEHOLDER)}
                  </Typography>
                  <ChipOptional size="small" />
                </Box>
              }
            >
              <AdScheduleContainerMeta
                BASIC_PATH={`${BASIC_PATH}`}
                control={control}
              />
            </CardContainer>
          </Grid>
        )}

        {/* Card 4: Titles */}
        <Grid
          Grid
          item
          xs={12}
          lg={showSlideChat ? 12 : 6}
          id={`${BASIC_PATH}.titles`}
        >
          <CardContainerWithCopy
            title={t(TITLES_META)}
            disabled={isGeneralDisabledFields}
            hasCopyValue={true}
            onCopyCallback={() => onCopyValuesInOtherAds({ type: "titles" })}
            id={`${BASIC_PATH}.titles`}
            copyText={COPY_VALUES_IN_OTHER_ADS}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {titles?.map((title, index) => (
                <TextFieldWithActions
                  key={title.id}
                  type="titles"
                  label={""}
                  name={`${BASIC_PATH}.titles.[${index}].value`}
                  disabled={isGeneralDisabledFields}
                  showAddIcon={
                    index === titles.length - 1 && index < maxTitles - 1
                  }
                  showDeleteIcon={titles.length > 1}
                  maxLength={maxLengthTitle}
                  platform={platform}
                  onChange={onGeneratePreview}
                  onAdd={() => onAddText({ type: "titles" })}
                  onRemove={() => onRemoveText({ type: "titles", index })}
                  onGenerate={async () => {
                    await onGenerateText({ type: "titles", index });
                  }}
                  control={control}
                  errors={errors}
                />
              ))}
            </Box>
          </CardContainerWithCopy>
        </Grid>
      </Grid>

      {/* Preview */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
        }}
      >
        <CardContainer
          dynamicHeight={true}
          title={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(ADS_CAMPAIGN_PREVIEW)}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                {!isGeneralDisabledFields && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <EditAdvantageEnhances
                      disabled={isGeneralDisabledFields}
                      adID={adID}
                      adPathForm={BASIC_PATH}
                      onCallbackSelect={(newAdvantageEnhances) => {
                        setValue(
                          `${BASIC_PATH}.advantageEnhances`,
                          newAdvantageEnhances,
                          { shouldValidate: true, shouldDirty: true }
                        );
                        onGeneratePreview();
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          }
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <AdPreviewMeta
              adID={adID}
              platform={platform}
              ref={refPreview}
              mediaUrls={mediaUrls}
              mediaData={mediaData}
              callToAction={callToAction}
              titles={titles}
              headlines={headlines}
              descriptions={descriptions}
              destinationUrls={destinationUrls}
              basicPath={BASIC_PATH}
              basicPathAdGroup={BASIC_PATH_AD_GROUP}
            />
          </Box>
        </CardContainer>
      </Box>
    </Box>
  );
};

const AdsAdNormalForm = ({
  objective,
  platform,
  breakColumnFormAndPreview,
  BASIC_PATH_AD_GROUP,
  BASIC_PATH,
  onAddText,
  onRemoveText,
  onGenerateText,
  isGeneralDisabledFields,
  errors,
  control,
  refPreview,
  onChangeIsCarousel,
  onGeneratePreview,
  onCopyValuesInOtherAds,
  platformData,
  onChangeCreativeSource,
  showCreativeSource,
  showSlideChat,
}) => {
  const { watch, setValue } = useFormContext();
  const { t } = useTranslationApp();

  const maxLengthTitle = ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform];
  ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[platform];
  const maxLengthHeadline = ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[platform];

  const maxTitles = maxTitlesPlatform;
  const maxHeadlines = maxHeadlinesPlatform;

  const adID = watch(`${BASIC_PATH}.id`);

  const language = watch("language");

  const isIOS14Campaign = platformData?.isIOS14Campaign;
  const isAppPromotionCampaign =
    objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION;

  const mediaUrls = watch(`${BASIC_PATH}.mediaUrls`);
  const mediaData = watch(`${BASIC_PATH}.mediaData`);
  const callToAction = watch(`${BASIC_PATH}.callToAction`);
  const titles = watch(`${BASIC_PATH}.titles`) || [{ value: "" }];
  const headlines = watch(`${BASIC_PATH}.headlines`) || [{ value: "" }];
  const descriptions = watch(`${BASIC_PATH}.descriptions`) || [{ value: "" }];
  const destinationType = watch(`${BASIC_PATH_AD_GROUP}.destinationType`);

  const status = watch(`${BASIC_PATH}.status`);
  const isAdCreated = status !== ADS_CAMPAIGN_STATUS_DRAFT;

  const showTrackingID = !(
    ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_META.includes(objective) &&
    destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION &&
    !platformData.isCatalogCampaign
  );

  const showAdSchedule = getShowAdScheduleMeta({ objective });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: breakColumnFormAndPreview ? "column" : "row",
        gap: 3,
        width: "100%",
        flexWrap: showSlideChat ? "wrap" : "nowrap",
      }}
    >
      {/* FORM */}
      <Grid
        container
        spacing={3}
        sx={{
          height: "min-content",
        }}
      >
        {/* Card Creative Source */}
        {showCreativeSource && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }} variant="h6">
                    {t(CREATIVE_SOURCE_PLACEHOLDER)}
                  </Typography>
                </Box>
              }
            >
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name={`${BASIC_PATH}.creativeSource`}
                  render={({ field }) => (
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={field.value}
                        onChange={(e) => {
                          onChangeCreativeSource(e.target.value);
                        }}
                      >
                        <FormControlLabel
                          value={ADS_CAMPAIGN_META_CREATIVE_SOURCE_TYPES.MANUAL}
                          control={<Radio />}
                          label={<Box>{t(MANUAL_UPLOAD_PLACEHOLDER)}</Box>}
                        />
                        <FormControlLabel
                          value={
                            ADS_CAMPAIGN_META_CREATIVE_SOURCE_TYPES.ADVANTAGE_CATALOG
                          }
                          control={<Radio />}
                          label={<Box>{t(ADVANTAGE_CATALOG_PLACEHOLDER)}</Box>}
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid>
            </CardContainer>
          </Grid>
        )}

        {/* Card Creative Type */}
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 600 }} variant="h6">
                  {t(CREATIVE_TYPE_PLACEHOLDER)}
                </Typography>
              </Box>
            }
          >
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Select
                  disabled={isGeneralDisabledFields || isAdCreated}
                  variant="filled"
                  name={`${BASIC_PATH}.isCarousel`}
                  doTranslate={true}
                  actionsChange={(value) => {
                    onChangeIsCarousel(value);
                  }}
                  control={control}
                  options={[
                    {
                      value: false,
                      label: INDIVIDUAL,
                    },
                    {
                      value: true,
                      label: CAROUSEL,
                    },
                  ]}
                />
              </Box>
            </Grid>
          </CardContainer>
        </Grid>

        {/* Card 2: Creative Type */}
        {!isIOS14Campaign && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }} variant="h6">
                    {t(DEEP_DESTINATION_APP_URL_PLACEHOLDER)}
                  </Typography>
                  <ChipOptional size="small" />
                </Box>
              }
            >
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <TextField
                    name={`${BASIC_PATH}.destinationUrls.[0].value`}
                    variant="filled"
                    actionsChange={onGeneratePreview}
                    fullWidth
                    disabled={isGeneralDisabledFields}
                    control={control}
                    errors={errors}
                  />
                  <ClickableText
                    disabled={isGeneralDisabledFields}
                    onClick={() =>
                      onCopyValuesInOtherAds({ type: "destinationUrls" })
                    }
                  >
                    {t(COPY_VALUES_IN_OTHER_ADS)}
                  </ClickableText>
                </Box>
              </Grid>
            </CardContainer>
          </Grid>
        )}

        {/* Card 3: Map Custom Page ID */}
        {isAppPromotionCampaign && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }} variant="h6">
                    {t(MAP_CUSTOM_PAGE_ID_PLACEHOLDER[destinationType])}
                  </Typography>
                  <ChipOptional size="small" />
                </Box>
              }
            >
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <TextField
                    name={`${BASIC_PATH}.appProductPageID`}
                    variant="filled"
                    fullWidth
                    disabled={isGeneralDisabledFields}
                    control={control}
                    errors={errors}
                  />
                  <ClickableText
                    disabled={isGeneralDisabledFields}
                    onClick={() =>
                      onCopyValuesInOtherAds({ type: "appProductPageID" })
                    }
                  >
                    {t(COPY_VALUES_IN_OTHER_ADS)}
                  </ClickableText>
                </Box>
              </Grid>
            </CardContainer>
          </Grid>
        )}

        {/* Card 4: Call to Action */}
        <Grid item xs={12}>
          <CardContainer
            aiGeneratedSection={{
              basePath: BASIC_PATH,
              section: "callToAction",
            }}
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 600 }} variant="h6">
                  {t(CALL_TO_ACTION_PLACEHOLDER)}
                </Typography>
              </Box>
            }
          >
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <SelectCallToActionMeta
                  disabled={isGeneralDisabledFields}
                  name={`${BASIC_PATH}.callToAction`}
                  fullWidth
                  objective={objective}
                  platform={platform}
                  destinationType={destinationType}
                  doTranslate={true}
                  control={control}
                  errors={errors}
                />
                <ClickableText
                  disabled={isGeneralDisabledFields}
                  onClick={() =>
                    onCopyValuesInOtherAds({ type: "callToAction" })
                  }
                >
                  {t(COPY_VALUES_IN_OTHER_ADS)}
                </ClickableText>
              </Box>
            </Grid>
          </CardContainer>
        </Grid>

        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(TRACKING_EVENTS)}
                </Typography>
                <ChipOptional size="small" />
              </Box>
            }
          >
            <Grid container spacing={2}>
              {showTrackingID && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1">
                      {t(TRACKING_WEBSITE_EVENTS)}
                    </Typography>
                    <SelectTrackingIDField
                      name={`${BASIC_PATH}.trackingID`}
                      disabled={isGeneralDisabledFields}
                      label={""}
                      fullWidth
                      variant="filled"
                      platform={platform}
                      integrationID={platformData?.integrationID}
                      adAccountID={platformData?.adAccountID}
                      control={control}
                      errors={errors}
                    />
                    <ClickableText
                      disabled={isGeneralDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "trackingID" })
                      }
                    >
                      {t(COPY_VALUES_IN_OTHER_ADS)}
                    </ClickableText>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography variant="body1">
                    {t(TRACKING_OFFLINE_EVENTS_PLACEHOLDER)}
                  </Typography>
                  <SelectTrackingIDField
                    name={`${BASIC_PATH}.trackingOfflineID`}
                    disabled={isGeneralDisabledFields}
                    label={""}
                    fullWidth
                    variant="filled"
                    platform={platform}
                    integrationID={platformData?.integrationID}
                    adAccountID={platformData?.adAccountID}
                    control={control}
                    errors={errors}
                  />
                  <ClickableText
                    disabled={isGeneralDisabledFields}
                    onClick={() =>
                      onCopyValuesInOtherAds({ type: "trackingOfflineID" })
                    }
                  >
                    {t(COPY_VALUES_IN_OTHER_ADS)}
                  </ClickableText>
                </Box>
              </Grid>
            </Grid>
          </CardContainer>
        </Grid>

        {showAdSchedule && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t(AD_SCHEDULE_PLACEHOLDER)}
                  </Typography>
                  <ChipOptional size="small" />
                </Box>
              }
            >
              <AdScheduleContainerMeta
                BASIC_PATH={`${BASIC_PATH}`}
                control={control}
              />
            </CardContainer>
          </Grid>
        )}

        {/* Card: Titles and Headlines */}
        <Grid container item xs={12} spacing={3}>
          {/* Card: Titles */}
          <Grid
            item
            xs={12}
            lg={showSlideChat ? 12 : 6}
            id={`${BASIC_PATH}.titles`}
          >
            <CardContainerWithCopy
              title={t(TITLES_META)}
              disabled={isGeneralDisabledFields}
              hasCopyValue={true}
              onCopyCallback={() => onCopyValuesInOtherAds({ type: "titles" })}
              id={`${BASIC_PATH}.titles`}
              copyText={COPY_VALUES_IN_OTHER_ADS}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {titles?.map((title, index) => (
                  <TextFieldWithActions
                    labelTitle={t(TITLE_PLACEHOLDER) + " " + (index + 1)}
                    key={title.id}
                    type="titles"
                    name={`${BASIC_PATH}.titles.[${index}].value`}
                    disabled={isGeneralDisabledFields}
                    showAddIcon={
                      index === titles.length - 1 && index < maxTitles - 1
                    }
                    showDeleteIcon={titles.length > 1}
                    platform={platform}
                    onChange={onGeneratePreview}
                    onAdd={() => onAddText({ type: "titles" })}
                    onRemove={() => onRemoveText({ type: "titles", index })}
                    maxLength={maxLengthTitle}
                    onGenerate={async () => {
                      await onGenerateText({ type: "titles", index });
                    }}
                    control={control}
                    errors={errors}
                  />
                ))}
              </Box>
            </CardContainerWithCopy>
          </Grid>

          {/* Card: Headlines */}
          <Grid
            item
            xs={12}
            lg={showSlideChat ? 12 : 6}
            id={`${BASIC_PATH}.headlines`}
          >
            <CardContainerWithCopy
              title={t(HEADLINES)}
              disabled={isGeneralDisabledFields}
              hasCopyValue={true}
              onCopyCallback={() =>
                onCopyValuesInOtherAds({ type: "headlines" })
              }
              id={`${BASIC_PATH}.headlines`}
              copyText={COPY_VALUES_IN_OTHER_ADS}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {headlines?.map((headline, index) => (
                  <TextFieldWithActions
                    labelTitle={t(HEADLINE_PLACEHOLDER) + " " + (index + 1)}
                    key={headline.id}
                    type="headlines"
                    name={`${BASIC_PATH}.headlines.[${index}].value`}
                    disabled={isGeneralDisabledFields}
                    platform={platform}
                    showAddIcon={
                      index === headlines.length - 1 && index < maxHeadlines - 1
                    }
                    showDeleteIcon={headlines.length > 1}
                    onChange={onGeneratePreview}
                    onAdd={() => onAddText({ type: "headlines" })}
                    onRemove={() => onRemoveText({ type: "headlines", index })}
                    maxLength={maxLengthHeadline}
                    onGenerate={async () => {
                      await onGenerateText({ type: "headlines", index });
                    }}
                    control={control}
                    errors={errors}
                  />
                ))}
              </Box>
            </CardContainerWithCopy>
          </Grid>
        </Grid>
      </Grid>

      {/* Preview */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
        }}
      >
        <CardContainer
          dynamicHeight={true}
          title={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="h6">{t(ADS_CAMPAIGN_PREVIEW)}</Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                {!isGeneralDisabledFields && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <EditAdvantageEnhances
                      disabled={isGeneralDisabledFields}
                      adID={adID}
                      adPathForm={BASIC_PATH}
                      onCallbackSelect={(newAdvantageEnhances) => {
                        setValue(
                          `${BASIC_PATH}.advantageEnhances`,
                          newAdvantageEnhances,
                          { shouldValidate: true, shouldDirty: true }
                        );
                        onGeneratePreview();
                      }}
                    />
                  </Box>
                )}
                <EditPlacementsMeta
                  language={language}
                  disabled={isGeneralDisabledFields}
                  destinationType={destinationType}
                  adID={adID}
                  adPathForm={BASIC_PATH}
                  adGroupPathForm={BASIC_PATH_AD_GROUP}
                  onCallbackSelect={(customizationPlacements) => {
                    const currentMediaData = watch(`${BASIC_PATH}.mediaData`);
                    if (!currentMediaData?.[0]) {
                      currentMediaData[0] = { from: "upload" };
                    }
                    currentMediaData[0].customizationPlacements =
                      customizationPlacements;
                    setValue(`${BASIC_PATH}.mediaData`, currentMediaData, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    onGeneratePreview();
                  }}
                />
              </Box>
            </Box>
          }
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <AdPreviewMeta
              ref={refPreview}
              adID={adID}
              mediaUrls={mediaUrls}
              mediaData={mediaData}
              callToAction={callToAction}
              titles={titles}
              headlines={headlines}
              descriptions={descriptions}
              platform={platform}
              basicPath={BASIC_PATH}
              basicPathAdGroup={BASIC_PATH_AD_GROUP}
            />
          </Box>
        </CardContainer>
      </Box>
    </Box>
  );
};

export default AdAppPromotionMetaForm;
